import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { completionToast } from "./RaiseInvoicePanel";

const ENDPOINTS = { job: "/jobs", emergency: "/emergency-jobs" };
const INVOICE_SOURCE = { job: "job", emergency: "emergency_job" };

/** One-tap "Completed": first click opens an inline confirm with an optional Client PO / WO # (auto-resets after 10 s). entity: "job" | "emergency". */
export default function QuickCompleteButton({ entity = "job", id, reference, status, onDone, className = "", testId, clientPoNumber = "", poRequired = false }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [po, setPo] = useState(clientPoNumber || "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!confirming) return;
    inputRef.current?.focus();
    const t = setTimeout(() => setConfirming(false), 10000);
    return () => clearTimeout(t);
  }, [confirming]);

  if (["completed", "cancelled"].includes(status)) return null;

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const complete = async (e) => {
    stop(e);
    setBusy(true);
    const previous = status || (entity === "emergency" ? "open" : "scheduled");
    const undo = async () => {
      try {
        await API.put(`${ENDPOINTS[entity]}/${id}/status`, { status: previous });
        toast.success(`${reference || "Job"} reverted to ${previous.replace(/_/g, " ")}`);
        onDone && onDone();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not undo");
      }
    };
    try {
      const body = { status: "completed" };
      if (po.trim() !== (clientPoNumber || "").trim()) body.client_po_number = po.trim();
      await API.put(`${ENDPOINTS[entity]}/${id}/status`, body);
      completionToast(navigate, INVOICE_SOURCE[entity], id, `${reference || "Job"} marked as completed${po.trim() ? ` · PO/WO ${po.trim()}` : ""}`, { onUndo: undo });
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark as completed");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (confirming) {
    return (
      <span className={`inline-flex items-center gap-1 ${className}`} onClick={stop} data-testid={`${testId || `quick-complete-${id}`}-confirm`}>
        <input ref={inputRef} value={po} onChange={(e) => setPo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") complete(e); if (e.key === "Escape") setConfirming(false); }}
          placeholder={poRequired ? "PO / WO # (required to invoice)" : "PO / WO # (optional)"} maxLength={60} title={poRequired ? "This client requires a PO/WO before the job can be invoiced — you can still complete it now" : undefined}
          className={`h-6 ${poRequired ? "w-40 border-amber-400" : "w-32 border-emerald-300"} rounded border bg-background px-1.5 text-[10px] font-mono`} data-testid={`${testId || `quick-complete-${id}`}-po`} />
        <Button size="sm" disabled={busy} onClick={complete} className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white" title="Confirm completion" data-testid={testId || `quick-complete-${id}`}>
          <CheckCircle size={11} className="mr-0.5" weight="fill" /> {busy ? "Saving…" : "Confirm"}
        </Button>
      </span>
    );
  }

  return (
    <Button size="sm" disabled={busy} onClick={(e) => { stop(e); setConfirming(true); }} variant="outline"
      className={`h-6 text-[10px] px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30 ${className}`}
      title="Mark as completed" data-testid={testId || `quick-complete-${id}`}>
      <CheckCircle size={11} className="mr-0.5" /> Completed
    </Button>
  );
}
